<script setup>
import { ref, computed } from 'vue';
import { Head, Link, useForm, router } from '@inertiajs/vue3';
import VHeaderBreadcrumb from '@/Shared/VHeaderBreadcrumb.vue';
import VAlert from '@/Shared/VAlert.vue';
import PdfViewer from './_partials/PdfViewer.vue';
import FieldConfigPanel from './_partials/FieldConfigPanel.vue';

const props = defineProps({
    title: String,
    additional: Object,
});

const template = computed(() => props.additional.template);

const breadcrumbs = [
    { url: props.additional.urlIndex, label: 'Inject Template' },
    { url: '#', label: 'Configure' },
];

const currentPage = ref(1);
const totalPages = ref(1);
const activeFieldId = ref(null);

// Template Config Form
const configForm = useForm({
    name: template.value.name,
    event_id: template.value.event_id,
    type: template.value.type,
    price: template.value.price,
    description: template.value.description,
    email_name: template.value.email_name,
    config: {
        ...(template.value.config || {}),
        is_ticket_database: template.value.config?.is_ticket_database || false,
        validate_unique_field: template.value.config?.validate_unique_field || '',
        is_ticket_api: template.value.config?.is_ticket_api || false,
        ticket_api_action_class: template.value.config?.ticket_api_action_class || '',
        custom_message: template.value.config?.custom_message || '',
        type: template.value.config?.type || '',
        is_running_number: template.value.config?.is_running_number || false,
        running_number_start: template.value.config?.running_number_start || null,
        running_number_sequence_pad: template.value.config?.running_number_sequence_pad || null,
        location: {
            x: template.value.config?.location?.x || null,
            xr: template.value.config?.location?.xr || null,
            y: template.value.config?.location?.y || null,
            yr: template.value.config?.location?.yr || null,
            page: template.value.config?.location?.page || 1,
        }
    },
    date_sign: {
        text: template.value.date_sign?.text || '',
        type: template.value.date_sign?.type || 'text',
        position: {
            x: template.value.date_sign?.position?.x || null,
            y: template.value.date_sign?.position?.y || null,
        },
        stack: {
            date: template.value.date_sign?.stack?.date || 'd / m / Y',
            reference: template.value.date_sign?.stack?.reference || '',
            function: template.value.date_sign?.stack?.function || '',
        }
    }
});

const onPageLoaded = (info) => {
    totalPages.value = info.totalPages;
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) currentPage.value++;
};

const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--;
};

// Generate markers for the current page
const currentMarkers = computed(() => {
    const markers = [];
    if (!template.value.document_field_config) return markers;

    template.value.document_field_config.forEach(field => {
        if (!field.config || !field.config.locations) return;
        
        field.config.locations.forEach((loc, index) => {
            if (loc.page === currentPage.value) {
                markers.push({
                    x: loc.x,
                    y: loc.y,
                    w: loc.w,
                    h: loc.h,
                    label: field.name,
                    type: activeFieldId.value === field.id ? 'active' : field.type,
                    originalType: field.type,
                    fieldId: field.id,
                    locIndex: index
                });
            }
        });
    });
    return markers;
});

const getFieldUpdateUrl = (fieldId) => {
    return props.additional.urlFieldUpdate.replace('__FIELD_ID__', fieldId);
};

const getFieldDeleteUrl = (fieldId) => {
    return props.additional.urlFieldDelete.replace('__FIELD_ID__', fieldId);
};

const handleCanvasClick = (coords) => {
    if (activeFieldId.value === null || activeFieldId.value === 'new') {
        return;
    }

    const field = template.value.document_field_config.find(f => f.id === activeFieldId.value);
    if (!field) return;

    const locations = [...(field.config?.locations || [])];
    
    const defaultFontSize = locations.length > 0 ? (locations[0].font_size || 9) : 9;

    let saveY = coords.y;
    const isTextType = !['sign', 'upload', 'attachment'].includes(field.type);
    
    // For text fields, FPDF uses Y as the baseline. 
    // If the user drew a bounding box, coords.y is the top edge. 
    // We add the height to store the bottom edge (baseline).
    if (isTextType && coords.h !== undefined) {
        saveY = coords.y + coords.h;
    }

    const payload = {
        x: coords.x,
        y: saveY,
        page: coords.page,
        font_size: defaultFontSize
    };
    
    if (coords.w !== undefined && coords.h !== undefined) {
        payload.w = coords.w;
        payload.h = coords.h;
    }

    locations.push(payload);

    const updateUrl = getFieldUpdateUrl(field.id);

    useForm({
        name: field.name,
        code: field.code,
        type: field.type,
        rules: field.rules || [],
        options: field.options || [],
        config: { ...field.config, locations: locations }
    }).put(updateUrl, {
        preserveScroll: true
    });
};

const saveTemplateConfig = () => {
    configForm.put(props.additional.urlUpdate, {
        preserveScroll: true
    });
};
</script>

<template>
    <Head>
        <title>{{ title }}</title>
    </Head>

    <div class="p-4 page-bg">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <VHeaderBreadcrumb :breadcrumbs="breadcrumbs" class="mb-0" />
            
            <button class="btn btn-premium btn-sm" data-bs-toggle="modal" data-bs-target="#templateConfigModal">
                <i class="fas fa-sliders-h me-2"></i> Template Settings
            </button>
        </div>

        <VAlert class="mb-3" />

        <div class="row g-4 editor-container">
            <!-- Left Side: PDF Viewer -->
            <div class="col-lg-8 col-xl-7 d-flex flex-column">
                <div class="card border-0 custom-card flex-grow-1 d-flex flex-column">
                    <div class="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-shrink-0">
                        <div class="fw-bold text-dark d-flex align-items-center">
                            <i class="far fa-file-pdf text-danger me-2 fs-5"></i>
                            {{ template.name }}
                            <span class="badge bg-light text-dark border ms-3 fw-normal font-monospace">ID: {{ template.id }}</span>
                        </div>
                        
                        <div class="d-flex align-items-center gap-2">
                            <button @click="prevPage" class="btn btn-sm btn-light border" :disabled="currentPage <= 1">
                                <i class="fas fa-chevron-left"></i>
                            </button>
                            <span class="small fw-bold px-2">Page {{ currentPage }} / {{ totalPages }}</span>
                            <button @click="nextPage" class="btn btn-sm btn-light border" :disabled="currentPage >= totalPages">
                                <i class="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                    
                    <div class="card-body p-0 bg-light flex-grow-1 position-relative">
                        <PdfViewer 
                            :pdfUrl="additional.urlPreview"
                            :page="currentPage"
                            :markers="currentMarkers"
                            @page-loaded="onPageLoaded"
                            @canvas-click="handleCanvasClick"
                        />
                    </div>
                </div>
            </div>

            <!-- Right Side: Field Config Panel -->
            <div class="col-lg-4 col-xl-5 d-flex flex-column">
                <div class="card border-0 custom-card flex-grow-1 d-flex flex-column">
                    <div class="card-header bg-white border-bottom py-3 flex-shrink-0">
                        <h6 class="mb-0 fw-bold text-dark"><i class="fas fa-list-ul me-2 text-brand"></i> Fields</h6>
                    </div>
                    <div class="card-body p-3 overflow-hidden d-flex flex-column flex-grow-1" style="min-height: 0;">
                        <FieldConfigPanel 
                            :template="template" 
                            :activeFieldId="activeFieldId"
                            :currentPage="currentPage"
                            :urlFieldStore="additional.urlFieldStore"
                            :urlFieldUpdate="additional.urlFieldUpdate"
                            :urlFieldDelete="additional.urlFieldDelete"
                            :urlFieldReorder="additional.urlFieldReorder"
                            :fieldSuggestions="additional.fieldSuggestions"
                            @select-field="id => activeFieldId = id"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Template Settings Modal -->
        <div class="modal fade" id="templateConfigModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content border-0 shadow">
                    <div class="modal-header border-bottom-0 pb-0">
                        <h5 class="modal-title fw-bold text-brand"><i class="fas fa-sliders-h me-2"></i> Template Advanced Settings</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="saveTemplateConfig">
                            
                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Template Name</label>
                                    <input v-model="configForm.name" type="text" class="form-control" required>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Event ID</label>
                                    <input v-model="configForm.event_id" type="number" class="form-control" required>
                                </div>
                            </div>

                            <hr class="text-muted opacity-25">

                            <h6 class="fw-bold mb-3">Ticket Validation Settings</h6>
                            <div class="row mb-3 align-items-center">
                                <div class="col-md-4">
                                    <div class="form-check form-switch mb-2">
                                        <input class="form-check-input" type="checkbox" id="isTicketDatabase" v-model="configForm.config.is_ticket_database">
                                        <label class="form-check-label small fw-bold text-muted" for="isTicketDatabase">Is Ticket DB (Internal)</label>
                                    </div>
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" id="isTicketApi" v-model="configForm.config.is_ticket_api">
                                        <label class="form-check-label small fw-bold text-muted" for="isTicketApi">Is Ticket API (External)</label>
                                    </div>
                                </div>
                                <div class="col-md-8">
                                    <div class="mb-2" v-if="configForm.config.is_ticket_database || configForm.config.is_ticket_api">
                                        <label class="form-label small fw-bold text-muted">Validate Unique Field</label>
                                        <input v-model="configForm.config.validate_unique_field" type="text" class="form-control form-control-sm" placeholder="e.g. registration_id">
                                    </div>
                                    <div class="mb-2" v-if="configForm.config.is_ticket_api">
                                        <label class="form-label small fw-bold text-muted">API Action Class</label>
                                        <input v-model="configForm.config.ticket_api_action_class" type="text" class="form-control form-control-sm" placeholder="e.g. CheckTicketTesyaFest::class">
                                    </div>
                                    <div v-if="configForm.config.is_ticket_database || configForm.config.is_ticket_api">
                                        <label class="form-label small fw-bold text-muted">Custom Not Found Message</label>
                                        <input v-model="configForm.config.custom_message" type="text" class="form-control form-control-sm" placeholder="Ticket Not Found / Error Message">
                                    </div>
                                </div>
                            </div>
                            
                            <hr class="text-muted opacity-25">

                            <h6 class="fw-bold mb-3">Document Type & Running Number</h6>
                            <div class="row mb-3 align-items-center">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Document Type</label>
                                    <select v-model="configForm.config.type" class="form-select form-select-sm">
                                        <option value="">Single (Default)</option>
                                        <option value="multiple">Multiple (e.g. Invoices, Tickets)</option>
                                    </select>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-check form-switch mt-4">
                                        <input class="form-check-input" type="checkbox" id="isRunningNumber" v-model="configForm.config.is_running_number">
                                        <label class="form-check-label small fw-bold text-muted" for="isRunningNumber">Enable Running Number</label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="configForm.config.is_running_number" class="row mb-3 p-3 bg-light rounded">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Starting Sequence Number</label>
                                    <input v-model.number="configForm.config.running_number_start" type="number" class="form-control form-control-sm" placeholder="Default: 1">
                                    <div class="form-text" style="font-size: 0.7rem;">Only applies if this template hasn't generated any documents yet.</div>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Sequence Padding (Zeros)</label>
                                    <input v-model.number="configForm.config.running_number_sequence_pad" type="number" class="form-control form-control-sm" placeholder="e.g. 3 (001, 002)">
                                </div>
                            </div>
                            
                            <hr class="text-muted opacity-25">
                            
                            <h6 class="fw-bold mb-3">Stamp Location Settings</h6>
                            <div class="row mb-3">
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">X</label>
                                    <input v-model.number="configForm.config.location.x" type="number" step="0.1" class="form-control form-control-sm">
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">XR</label>
                                    <input v-model.number="configForm.config.location.xr" type="number" step="0.1" class="form-control form-control-sm">
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">Y</label>
                                    <input v-model.number="configForm.config.location.y" type="number" step="0.1" class="form-control form-control-sm">
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">YR</label>
                                    <input v-model.number="configForm.config.location.yr" type="number" step="0.1" class="form-control form-control-sm">
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Stamp Page</label>
                                    <input v-model.number="configForm.config.location.page" type="number" class="form-control form-control-sm">
                                </div>
                            </div>
                            

                            
                            <hr class="text-muted opacity-25">
                            
                            <h6 class="fw-bold mb-3">Date Sign Settings</h6>
                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Text</label>
                                    <input v-model="configForm.date_sign.text" type="text" class="form-control form-control-sm" placeholder="Text to show">
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted">Type</label>
                                    <select v-model="configForm.date_sign.type" class="form-select form-select-sm">
                                        <option value="text">Text</option>
                                        <option value="stack">Stack</option>
                                    </select>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">Position X</label>
                                    <input v-model.number="configForm.date_sign.position.x" type="number" step="0.1" class="form-control form-control-sm no-arrow">
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label small fw-bold text-muted">Position Y</label>
                                    <input v-model.number="configForm.date_sign.position.y" type="number" step="0.1" class="form-control form-control-sm no-arrow">
                                </div>
                            </div>
                            <div v-if="configForm.date_sign.type === 'stack'" class="row mb-3">
                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Stack Date Format</label>
                                    <input v-model="configForm.date_sign.stack.date" type="text" class="form-control form-control-sm" placeholder="e.g. d / m / Y">
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Stack Reference</label>
                                    <input v-model="configForm.date_sign.stack.reference" type="text" class="form-control form-control-sm" placeholder="Reference">
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label small fw-bold text-muted">Stack Function</label>
                                    <input v-model="configForm.date_sign.stack.function" type="text" class="form-control form-control-sm" placeholder="Function">
                                </div>
                            </div>

                            <div class="d-flex justify-content-end mt-4">
                                <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" class="btn btn-premium" :disabled="configForm.processing">
                                    <span v-if="configForm.processing" class="spinner-border spinner-border-sm me-2"></span>
                                    Save Settings
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.page-bg {
    background-color: #f7f9fc;
    min-height: calc(100vh - 60px);
    display: flex;
    flex-direction: column;
}
.editor-container {
    flex-grow: 1;
    min-height: 0;
}
.custom-card {
    border-radius: 12px !important;
    background: #ffffff;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03) !important;
    border: 1px solid rgba(0, 0, 0, 0.05) !important;
}
.text-brand { color: #8a2529 !important; }
.btn-premium {
    background: linear-gradient(135deg, #8a2529 0%, #aa3a3e 100%);
    color: #fff;
    border: none;
}
.btn-premium:hover { background: linear-gradient(135deg, #741d20 0%, #8a2529 100%); color: #fff; }
</style>
